import React from 'react';
import * as Form from 'react-hook-form';
import { Keyboard } from 'react-native';
import { useDispatch } from 'react-redux';
import { t } from '@lingui/macro';
import { useNavigation } from '@react-navigation/native';

import SignUpUsernamePassword from '@src/api/SignUpUsernamePassword';
import { passwordRegex, usernameRegex } from '@src/helpers';
import useAuthenticationTokens from '@src/hooks/useAuthenticationTokens';
import useCheckUniqueUsername from '@src/hooks/useCheckUniqueUsername';
// import useLabelsFilled from '@src/hooks/useLabelsFilled';
import useSnackBar from '@src/hooks/useSnackBar';
import { i18n, i18yup } from '@src/i18n';
import { setSetupComplete } from '@src/store/authentication/actions';

import { FormData } from './types';

const useForm = () => {
  const navigation = useNavigation();
  //  const labelsFilled = useLabelsFilled();
  const snackBar = useSnackBar();

  const {
    tokens: { accessToken },
  } = useAuthenticationTokens();
  const checkUniqueUsername = useCheckUniqueUsername();
  const dispatcher = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const form = Form.useForm<FormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    validationSchema: i18yup.object<FormData>({
      username: i18yup
        .string()
        .matches(usernameRegex, i18n._(t`Username must be alphanumeric`))
        .required()
        .label('Username'),
      password: i18yup
        .string()
        .matches(
          passwordRegex,
          i18n._(t`Password must be at least 6 characters`)
        )
        .required()
        .label('Password'),
      confirmPassword: i18yup
        .string()
        .matches(
          passwordRegex,
          i18n._(t`Password must be at least 6 characters`)
        )
        .equals([i18yup.ref('password')], i18n._(t`Passwords does not match`))
        .required()
        .label('Password'),
    }),
  });

  const handleSubmit = React.useCallback(
    async (data: FormData) => {
      checkUniqueUsername(data.username)
        .then(async (isUniqueUsername) => {
          try {
            Keyboard.dismiss();
            setLoading(true);

            if (!isUniqueUsername) {
              snackBar.show({ content: i18n._(t`Username must be unique`) });
              return;
            }

            await SignUpUsernamePassword(accessToken, data);
            dispatcher(setSetupComplete(true));
            // if (!labelsFilled.filled) {
            // @ts-ignore
            // navigation.replace('SetupTalents');
            navigation.replace('AuthenticatedStack');
            // }
          } catch (e) {
            console.log(e);
            setLoading(false);
          }
        })
        .catch((error) => {
          snackBar.show({ content: i18n._(t`Username must be unique`) });
          console.log('[Error] checkUniqueUsername: ', error);
        });
    },
    [
      accessToken,
      checkUniqueUsername,
      dispatcher,
      // labelsFilled.filled,
      navigation,
      snackBar,
    ]
  );

  return React.useMemo(
    () => ({
      form,
      handleSubmit: form.handleSubmit(handleSubmit),
      loading,
    }),
    [form, handleSubmit, loading]
  );
};

export default useForm;
